<template>
	<div :class="{'container-fluid':!isSettingsComponent && !isWizardInstance}">
		<freemium-locked-alert v-if="$store.company.is_freemium == 1"></freemium-locked-alert>
		<onboarding-card v-if="!isWizardInstance" title="Compensation Group Setup" name="Compensation Group Setup" setting-key="unionSetup" v-on:sectionComplete="setupCompleteModalVisible = true">
			<p>Compensation Groups allow you to set specific working terms for groups of employees, such as how mileage is paid, special pay rates, and per diem payouts.</p>
		</onboarding-card>

		<div style="margin: auto;max-width: 1200px;">

			<div v-if="selectedUnion">
				<div class="d-flex flex-row justify-content-between align-items-center mb-2">
					<a href="#" v-on:click.prevent="backToList">
						<i class="fa fa-arrow-left"></i>
						Back to Compensation Groups
					</a>
					
					<button class="btn btn-success" v-on:click="saveUnion('fromEdit')">
						<i class="fa fa-save"></i>
						Save Comp. Group
					</button>

				</div>

				<field label="Compensation Group Name" 
					col-class="col-md-3"  
					v-model="selectedUnion.union_name">
				</field>

				<field label="Color"
					col-class="col-md-3"
					:params="{type:'color-picker',colors:['#408fbf','#8440bf','#bf40a3','#bf4040','#bfab40','#40bf85']}"
					v-model="selectedUnion.text_color">
				</field>

				<label v-if="!isWizardInstance" class="offset-md-3 mt-2">
					<input type="checkbox" class="ml-2" v-model="showArchivedCAs" v-on:change="fetchAndSetUnions">
					Show archived compensation schedules
				</label>

				<collective-agreements-component 
					:is-wizard-instance="isWizardInstance"
					:selected-union="selectedUnion"
					:collective-agreements="selectedUnion.CollectiveAgreement"
					:unions="unions"
					:employee-classifications="classifications"
					:time-log-settings="timeLogSettings"
					:company-addresses="companyAddresses"
					v-on:modelChange="fetchData"
					v-on:save="saveUnion('fromEdit')">

					<a href="#" class="text-danger" v-on:click.prevent="confirmDeleteUnion">
						<i class="fa fa-trash"></i>
						Delete Compensation Group
					</a>

				</collective-agreements-component>
				<hr>
				<widget v-if="!isWizardInstance" title="Migrate a Compensation Schedule" icon="fa-long-arrow-alt-right">
					
					<h4>Instructions</h4>

					<p>This will help you move to a new compensation schedule when your terms update. All projects & services attached to the selected 'old' compensation schedule will be changed over to the selected 'new' compensation schedule.</p>

					<p>If a date is set, time logs under the old compensation schedule will be retro-actively updated from that date forward, to link to the new compensation schedule. <strong>NOTE:</strong> You cannot schedule a future effective date; the effective date must be in the past.</p>

					<field label="Old Compensation Schedule" col-class="col-lg-4 col-md-6"
						v-model="caMigrate.payload.old_ca_id"
						:params="{type:'select',options:caOptions}"
						:invalid="$store.errorList.old_ca">
					</field>

					<field label="New Compensation Schedule" col-class="col-lg-4 col-md-6"
						v-model="caMigrate.payload.new_ca_id"
						:params="{type:'select',options:caOptions}"
						:invalid="$store.errorList.new_ca">
					</field>

					<field label="Effective Date" col-class="col-lg-4 col-md-6"
						v-model="caMigrate.payload.effective_date"
						:params="{type:'date'}"
						:invalid="$store.errorList.effective_date">
					</field>

					<button class="btn btn-success float-right" v-on:click="caMigrate.confirmModalVisible = true">
						<i class="fa fa-arrow-right"></i>
						Migrate Compensation Schedule
					</button>
					<div style="clear: both;"></div>
				</widget>

				<modal-dialog :modal-visible="caMigrate.confirmModalVisible"
					title="Compensation Schedule Migration"
					confirm-button-text="Yes, Proceed"
					:cancel-button-text="caMigrate.results ? 'Done' : 'Cancel'"
					:submit-visible="!caMigrate.results"
					:working="caMigrate.working"
					v-on:confirm="migrateCA"
					v-on:closeModal="closeMigrate">

					<div v-if="!caMigrate.results" class="text-center">
						<p>You are migrating jobs & services set with:

							<div class="d-flex flex-column ">
								<strong>{{getCATitle(caMigrate.payload.old_ca_id)}}</strong>
								to
								<strong>{{getCATitle(caMigrate.payload.new_ca_id)}}</strong>
							</div>
						</p>

						<p v-if="caMigrate.effective_date">Time logs from <strong>{{caMigrate.effective_date}}</strong> forward will be updated with the new compensation schedule.</p>
						<p v-else>No time logs will be retro-actively updated.</p>

						<p>Would you like to proceed with the compensation schedule migration?</p>
					</div>
					<div v-else>
						<h4>Migration Results</h4>
						<ul>
							<li><strong>Projects Changed:</strong> {{caMigrate.results.jobs}}</li>
							<li><strong>Services Changed:</strong> {{caMigrate.results.services}}</li>
							<li><strong>Time Logs Changed:</strong> {{caMigrate.results.time_logs}}</li>
						</ul>
					</div>

				</modal-dialog>
			</div>
			<div v-else>

				<div class="d-flex flex-row justify-content-between align-items-center mb-2">
					<h3>Compensation Groups</h3>

					<button class="btn btn-success" v-on:click="newUnion">
						<i class="fa fa-plus"></i>
						New Compensation Group
					</button>

				</div>

				<ul class="list-group union-list text-center mb-2">
					<li v-for="union in unions" class="list-group-item" :class='{active: selectedUnion && selectedUnion.id == union.id}' v-on:click="editUnion(union)">
						<i v-if="selectedUnion && selectedUnion.id == union.id" class="fa fa-chevron-right"></i>

						{{union.union_name}}

						<i v-if="!selectedUnion || selectedUnion && selectedUnion.id != union.id" class="fa fa-pencil-alt float-right mt-1"></i>
					</li>
				</ul>
			
			</div>

		</div>

		<modal-dialog
			:modal-visible="unionDialog.visible"
			title="New Compensation Group"
			confirm-button-text="Save"
			v-on:closeModal="unionDialog.visible = false"
			v-on:confirm="saveUnion">

			<field label="Compensation Group Name" col-class="stacked"  v-model="record.union_name"></field>

		</modal-dialog>

		<modal-dialog
			:modal-visible="deleteUnionDialog.visible"
			title="Delete Compensation Group"
			confirm-button-text="Yes, Delete"
			button-class="btn-outline-danger"
			v-on:closeModal="deleteUnionDialog.visible = false"
			v-on:confirm="deleteUnion">

			Are you sure you want to delete this compensation group?

		</modal-dialog>

		<modal-dialog
			:modal-visible="setupCompleteModalVisible"
			title="Initial Setup Complete!"
			:submit-visible="false"
			cancel-button-text="Finish Setup"
			v-on:closeModal="finishSetup">

			<div class="alert alert-info text-center">
				<h2 style="margin-bottom: 0">You're Ready to Go!</h2>

			</div>
			<p>Your company is all set up and ready to go! From here you can do any of the following:</p>

				<ul>
					<li><router-link to="/project/new/details">Create your first <strong>project</strong></router-link></li>
					<li><router-link to="/service/new/details">Create your first <strong>service</strong></router-link></li>
					<li><router-link to="/contacts">Add contacts & customers</router-link></li>
					<li><router-link to="/employees/list">Create employee accounts</router-link></li>
					<li><router-link to="/equipment/list">Start tracking equipment</router-link></li>
					<li><router-link to="/settings">View the rest of your settings</router-link></li>
				</ul>

		</modal-dialog>

	</div>
</template>

<script>
import SettingsToolbar from "./SettingsToolbar"
import Field from "@/components/Field"
import ModalDialog from "@/components/ModalDialog"
import TableList from "@/components/TableList"
import CollectiveAgreementsComponent from "@/components/super_components/CollectiveAgreementsComponent"
import PallettePicker from "@/components/PallettePicker"
import OnboardingCard from "@/components/OnboardingCard"
import FormGroup from "@/components/FormGroup"
import Widget from "@/components/Widget"
import FreemiumLockedAlert from "@/components/FreemiumLockedAlert"

export default {

	name: 'SettingsUnions',
	components: {
		SettingsToolbar,
		Field,
		TableList,
		CollectiveAgreementsComponent,
		ModalDialog,
		PallettePicker,
		OnboardingCard,
		FormGroup,
		Widget,
		FreemiumLockedAlert
	},
	props:["isSettingsComponent","isWizardInstance"],
	data() {
		return {
			editing: false,
			record: {
				id: null,
				union_name: null,
				slug: null
			},
			saveButtonText: "Add",
			fields: [
				{label: 'Name', key:'union_name'}
			],
			selectedUnion: null,
			showAllCollectiveAgreements: false,
			unions: [],
			classifications: [],
			unionDialog: {
				visible: false
			},
			deleteUnionDialog: {
				visible: false
			},
			timeLogSettings: {},
			test: "",
			setupCompleteModalVisible: false,
			showArchivedCAs: false,
			companyAddresses: [],
			caMigrate: {
				payload: {
					old_ca_id: null,
					new_ca_id: null,
					effective_date: null
				},
				confirmModalVisible: false,
				results: false,
				working: false
			},
			collectiveAgreements: []
		}
	},
	computed: {
		caOptions() {
			return this.optionify(this.collectiveAgreements,"title")
		},
		classificationOptions() {
			return this.optionify(this.classifications,"title")
		},
		unionNavItems() {
			return this.unions.map(union => {
				union.text = union.union_name

				return union
			})
		}
	},
	methods: {
		getCATitle(ca_id) {
			let ca = this.collectiveAgreements.find(ca => ca.id == ca_id)

			if (ca)
				return ca.title
			else
				return "[none]"
		},
		finishSetup() {
			this.$store.companySetupMode = false
			this.setupCompleteModalVisible = false
		},
		newUnion() {
			this.unionDialog.visible = true;
			this.record = {union_name: ""}

		},
		editUnion(record,skipPushState) {
			let vm = this
			this.record = record
			this.showAllCollectiveAgreements = false
			this.selectedUnion = record

			//don't use vue-router, because we don't actually need to load any resources, so this prevents the flash or components being re-loaded
			if (!skipPushState && !this.isWizardInstance)
				window.history.pushState({},"","/employees/settings/compensation-groups-unions/"+this.record.slug)

			//fetch CAs
			let params = {
				filters: [
					["union_id",this.selectedUnion.id]
				],
				fields: ["id","slug","title","expiry_date","UpdatedBy"],
				include: [{model: "User",fields:["id","slug","first_name","last_name"]}]
			}

			this.spinner(true)

			let promises = [
				this.getJSON("CollectiveAgreement/get",params),
				this.fetchClassifications(false)
			]

			Promise.all(promises).then(responses => {
				if (responses[0].success)
					vm.collectiveAgreements = responses[0].data

				this.editing = true

				vm.spinner(false)
			})

		},
		backToList() {
			if (!this.isWizardInstance)
				window.history.pushState({},"","/employees/settings/compensation-groups-unions")
			this.selectedUnion = false
		},
		confirmDeleteUnion() {

			if (this.selectedUnion.CollectiveAgreement.length > 0) {
				this.alertFail("You need to delete or move all the compensation schedules belonging to this compensation group before you can delete it")
			}
			else
				this.deleteUnionDialog.visible = true
		},
		deleteUnion() {
			let vm = this

			this.postData("Union/delete",{id: this.selectedUnion.id}).then(response => {
				vm.deleteUnionDialog.visible = false
				vm.selectedUnion = null

				vm.fetchData()
			})
		},
		closeMigrate() {
			this.caMigrate.confirmModalVisible = false
			this.caMigrate.results = false
		},
		migrateCA() {
			/*if (this.moment(this.caMigrate.effective_date).isAfter(this.moment())) {
				this.alertFail("Effective dates cannot be scheduled for the future")
				this.$store.errorList.effective_date = true
			}
			else
				this.$store.errorList.effective_date = false

			this.$forceUpdate()*/
			let vm = this

			this.caMigrate.working = true

			this.postData("CollectiveAgreement/migrate",this.caMigrate.payload,true).then(response => {
				vm.caMigrate.working = false
				if (response.success) {
					vm.caMigrate.results = response.data
				}
			})

		},
		saveUnion(type) {
			let vm = this

			let payload = {
				union_name: this.record.union_name,
				text_color: this.record.text_color
			}

			if (this.editing)
				payload.id = this.record.id

			if (type == 'fromEdit') {
				payload.union_name = this.selectedUnion.union_name,
				payload.text_color = this.selectedUnion.text_color,
				payload.id = this.selectedUnion.id
			}

			this.saveRecord("Union",payload,response => {
				vm.alertSaved("Compensation group saved!")

				if (type != 'fromEdit') {
					vm.record.union_name = ""
					vm.record.id = null
					vm.fetchData()
					vm.editing = false
					vm.unionDialog.visible = false
				}
				
			})
		},
		fetchAndSetUnions() {
			let vm = this
			return new Promise((resolve,reject) => {
				vm.fetchUnions(this.showArchivedCAs).then(response => {
					if (response.success) {
						vm.unions = response.data

						if (vm.selectedUnion) 
							vm.selectedUnion = vm.unions.find(union => union.id == vm.selectedUnion.id)
						
					}

					resolve(response)
				})
			})
		},
		fetchClassifications(spinner) {
			let vm = this

			if (spinner)
				this.spinner(true)

			let classificationParams = {
				filters: [
					["union_id",this.selectedUnion.id]
				]
			}

			return new Promise((resolve,reject) => {
				vm.getJSON("EmployeeClassification/get",classificationParams).then(response => {
					if (response.success)
						vm.classifications = response.data

					if (spinner)
						vm.spinner(false)

					resolve(response)
				})
			})
		},
		fetchData() {
			let vm = this

			let promises = [
				this.fetchAndSetUnions(),
				this.getJSON("TimeLogSetting/get"),
				this.getJSON("CompanyAddress/get")
			]

			if (this.selectedUnion) {
				let classificationParams = {
					filters: [
						["union_id",this.selectedUnion.id]
					]
				}

				promises.push(this.fetchClassifications(false))
			}

			this.spinner(true)

			return new Promise((resolve,reject) => {

				Promise.all(promises).then(responses => {
					vm.spinner(false)

					vm.companyAddresses = responses[2].data

					if (responses[1].data.length > 0)
						vm.timeLogSettings = responses[1].data[0]

					resolve(responses)

				})
			})
		}
	},
	created() {

		let vm = this

		this.fetchData().then(responses => {
			let segments = vm.segmentUrl(window.location.href)

			//if there is a segment after unions, then we have a specific union selected
			if (segments[segments.length-2] == 'unions' && segments.indexOf('unions') < segments.length - 1) {
				//find matching union
				let union = vm.unions.find(union => union.slug == segments[segments.length-1])

				if (union)
					vm.editUnion(union,true)
			}
		})
	}

}
</script>

<style type="text/css" scoped>

	.union-list .list-group-item:hover {
		cursor: pointer;
		background-color: #e0e0e0;
	}

	.list-group li.list-group-item.active {
		background-color: white;
		color: #424c50;
		border: 1px solid rgba(0,0,0,.125);
	}

	@media (min-width: 768px) {

		.list-group li.list-group-item.active {
			margin-right: -10px!important;
			border-bottom-right-radius: 0!important;
			border-top-right-radius: 0!important;
			border-right: none;
		}

	}

</style>